/**
 * The access token, in memory, for the life of the tab.
 *
 * A module singleton rather than Redux state, for one reason: the base query needs to read it
 * synchronously on every request, and importing the store from the base query creates a cycle
 * (store → api → baseQuery → store). It is still memory-only — a refresh of the page loses it,
 * which is correct, and the session is restored from the refresh cookie instead.
 *
 * It is never written to `localStorage`. That is the whole point.
 */

let accessToken: string | null = null;
let expiresAt = 0;

/** Refresh this far before actual expiry, so a request never races the clock. */
const SKEW_MS = 30_000;

export function setAccessToken(token: string, expiresAtIso: string): void {
  accessToken = token;
  expiresAt = Date.parse(expiresAtIso);
}

export function clearAccessToken(): void {
  accessToken = null;
  expiresAt = 0;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function isAccessTokenExpiring(now = Date.now()): boolean {
  return accessToken === null || now >= expiresAt - SKEW_MS;
}

/**
 * The raw expiry epoch-ms, for a UI that wants to count down rather than just ask a yes/no
 * question — `useSessionExpiryWarning` is the only consumer today. `0` means no token is held.
 */
export function getAccessTokenExpiresAt(): number {
  return accessToken === null ? 0 : expiresAt;
}

/**
 * The CSRF token, mirrored from the readable `pos_csrf` cookie into a header on every write.
 *
 * Double-submit: an attacker's page can cause the browser to *send* our cookies, but cannot read
 * them to construct the matching header. Reading it per request rather than caching it means a
 * rotation on renew takes effect immediately.
 */
export function csrfToken(): string | null {
  const match = /(?:^|;\s*)pos_csrf=([^;]+)/.exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
